import { createHook, createStore } from 'react-sweet-state'
import { getCurrentUser, patchUserAcceptance } from 'api/rest/userRequests'
import { ResponsePayload } from 'auth'
import { navigate } from 'gatsby'
import Cookies from 'js-cookie'
import routes from 'routes/routes'
import { User } from 'users'

import { getUserFromToken, setTokenCookies } from '../../services/auth/auth'

const colors = [
    '#ad2102',
    '#391085',
    '#006d75',
    '#9e1068',
    '#ad4e00',
    '#7c5914',
    '#003860',
    '#c0392b',
    '#536d13',
    '#391085',
    '#29003a',
    '#0050b3',
    '#590154',
    '#a8071a',
]

export const getInitials = (string: string) => {
    return string.slice(0, 2).toUpperCase()
}

const convertInitialsToNumber = (text: string) => {
    const charCodes = text
        .split('')
        .map((char) => char.charCodeAt(0))
        .join('')
    return parseInt(charCodes)
}

export const getAvatarColorFromUsername = (username: string) =>
    colors[convertInitialsToNumber(getInitials(username)) % colors.length]

const Store = createStore({
    initialState: {
        isAuthenticated: Cookies.get('access_token') ? true : false,

        user: {
            name: getUserFromToken().username,
            roles: getUserFromToken().roles,
            avatar: getInitials(getUserFromToken().username),
            color: getAvatarColorFromUsername(getUserFromToken().username),
            gdprAccepted: false,
            termsAndConditionsAccepted: false,
            isAcceptanceChecked: false,
        },
    },

    actions: {
        checkAcceptance:
            () =>
            async ({ getState, setState }) => {
                const {
                    gdprAccepted = false,
                    termsAndConditionsAccepted = false,
                } =
                    (await getCurrentUser()
                        .then((res) => res.data)
                        .catch(() => null)) || {}

                setState({
                    user: {
                        ...getState().user,
                        isAcceptanceChecked: true,
                        gdprAccepted,
                        termsAndConditionsAccepted,
                    },
                })
            },

        acceptTermsAndConditions:
            (
                consents: Pick<
                    User,
                    'gdprAccepted' | 'termsAndConditionsAccepted'
                >,
                onError: () => void
            ) =>
            async ({ setState, getState }) => {
                await patchUserAcceptance(consents)
                    .then(() => {
                        setState({
                            user: {
                                ...getState().user,
                                isAcceptanceChecked: true,
                                ...consents,
                            },
                        })
                    })
                    .catch(() => onError())
            },
        logIn:
            (data: ResponsePayload) =>
            async ({ setState }) => {
                setTokenCookies(data)
                const { username, roles } = getUserFromToken()
                const user = await getCurrentUser()
                    .then((res) => res.data)
                    .catch(() => null)

                const {
                    gdprAccepted = false,
                    termsAndConditionsAccepted = false,
                } = user || {}

                setState({
                    isAuthenticated: true,
                    user: {
                        name: username,
                        roles,
                        avatar: getInitials(username),
                        color: getAvatarColorFromUsername(username),
                        gdprAccepted,
                        termsAndConditionsAccepted,
                        isAcceptanceChecked: true,
                    },
                })
                navigate(routes.home)
            },

        logOut:
            () =>
            ({ setState }) => {
                Cookies.remove('access_token')
                Cookies.remove('refresh_token')
                setState({
                    isAuthenticated: false,
                    user: {
                        name: '',
                        roles: [],
                        avatar: '',
                        color: '',
                        gdprAccepted: false,
                        termsAndConditionsAccepted: false,
                        isAcceptanceChecked: false,
                    },
                })
                navigate(routes.home)
            },
    },

    name: 'Auth store',
})

export const useAuth = createHook(Store)
